import React from "react";
import { DateTime } from "luxon";
import { McInput } from "@maersk-global/mds-react-wrapper/components-core/mc-input";
import { McTextarea } from "@maersk-global/mds-react-wrapper/components-core/mc-textarea";
import { McSelect, McOption } from "@maersk-global/mds-react-wrapper";
import { McTypeahead } from "@maersk-global/mds-react-wrapper/components-core/mc-typeahead";
import { McInputDate } from "@maersk-global/mds-react-wrapper/components-core/mc-input-date";
import { TRIGGER_OPTIONS, STATUS_OPTIONS, DEPLOYMENT_OPTIONS } from "./constants";

export default function ApplicationFormFields({
  formData,
  appNameOptions,
  appNameTypeaheadRef,
  onAppIdInput,
  onAppNameInput,
  onAppNameSelected,
  onIncidentInput,
  onFormChange,
  isEditMode = false,
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          App ID
        </label>
        <McInput
          hiddenlabel={true}
          value={formData.app_id}
          input={(e) => onAppIdInput(e.target.value)}
          placeholder="Enter App ID"
          fit="medium"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          App Instance Name <span style={{ color: "#d32f2f" }}>*</span>
        </label>
        <McTypeahead
          ref={appNameTypeaheadRef}
          hiddenlabel={true}
          value={formData.app_name}
          data={appNameOptions}
          input={(e) => onAppNameInput(e.target.value)}
          optionselected={onAppNameSelected}
          placeholder="Enter or select App Instance Name"
          showlistonfocus={true}
          clearbutton={true}
          maxoptions="10"
          fit="medium"
          nosuggestions="Enter App ID/Name to autofill related details"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Platform <span style={{ color: "#d32f2f" }}>*</span>
        </label>
        <McInput
          hiddenlabel={true}
          value={formData.platform}
          input={(e) => onFormChange("platform", e.target.value)}
          placeholder="Auto-filled"
          fit="medium"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Portfolio <span style={{ color: "#d32f2f" }}>*</span>
        </label>
        <McInput
          hiddenlabel={true}
          value={formData.portfolio}
          input={(e) => onFormChange("portfolio", e.target.value)}
          placeholder="Auto-filled"
          fit="medium"
        />
      </div>

      {/* Status */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Status <span style={{ color: "#d32f2f" }}>*</span>
        </label>
        <McSelect
          hiddenlabel={true}
          value={formData.status}
          optionselected={(e) => onFormChange("status", e.detail?.value || e.detail)}
          fit="medium"
        >
          {STATUS_OPTIONS.map((option) => (
            <McOption
              key={option.value}
              value={option.value}
              selected={formData.status === option.value}
            >
              {option.label}
            </McOption>
          ))}
        </McSelect>
      </div>

      {/* Trigger */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Trigger <span style={{ color: "#d32f2f" }}>*</span>
        </label>
        <McSelect
          hiddenlabel={true}
          value={formData.trigger_type}
          optionselected={(e) => onFormChange("trigger_type", e.detail?.value || e.detail)}
          placeholder="Select Trigger"
          fit="medium"
        >
          {TRIGGER_OPTIONS.map((option) => (
            <McOption
              key={option.value}
              value={option.value}
              selected={formData.trigger_type === option.value}
            >
              {option.label}
            </McOption>
          ))}
        </McSelect>
      </div>

      {/* Incident ID - Only show in Add mode */}
      {!isEditMode && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
            Incident ID <span style={{ color: "#d32f2f" }}>*</span>
          </label>
          <McInput
            hiddenlabel={true}
            value={formData.incident}
            input={(e) => onIncidentInput(e.target.value)}
            placeholder="Enter Incident ID"
            fit="medium"
          />
        </div>
      )}

      {/* Priority - Only show in Add mode */}
      {!isEditMode && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
            Priority <span style={{ color: "#d32f2f" }}>*</span>
          </label>
          <McInput
            hiddenlabel={true}
            value={formData.priority}
            disabled
            placeholder="Auto-filled"
            fit="medium"
          />
        </div>
      )}

      {/* Incident Date - Only show in Add mode */}
      {!isEditMode && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
            Incident Date
          </label>
          <McInput
            hiddenlabel={true}
            value={formData.incident_dates}
            disabled
            placeholder="Auto-filled"
            fit="medium"
          />
        </div>
      )}

      {/* Added to Red Channel */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Added to Red Channel Date
        </label>
        <McInputDate
          hiddenlabel={true}
          required={true}
          placeholder="DD-MM-YYYY"
          format="DD-MM-YYYY"
          value={formData.added_to_red_channel ? formData.added_to_red_channel.toFormat('dd-MM-yyyy') : ""}
          name="added_to_red_channel"
          inputdateselected={(e) => {
            const dateValue = e.detail || e.target?.value;
            if (dateValue) {
              const date = DateTime.fromFormat(dateValue, 'dd-MM-yyyy', { zone: 'utc' });
              if (date.isValid) {
                onFormChange("added_to_red_channel", date);
              }
            }
          }}
        />
      </div>

      {/* Moved to Green Channel */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Moved to Green Channel Date
        </label>
        <McInputDate
          hiddenlabel={true}
          required={formData.status === "Green"}
          placeholder="DD-MM-YYYY"
          format="DD-MM-YYYY"
          value={formData.moved_to_green_channel_on ? formData.moved_to_green_channel_on.toFormat('dd-MM-yyyy') : ""}
          name="moved_to_green_channel_on"
          disabled={formData.status !== "Green"}
          inputdateselected={(e) => {
            const dateValue = e.detail || e.target?.value;
            if (dateValue) {
              const date = DateTime.fromFormat(dateValue, 'dd-MM-yyyy', { zone: 'utc' });
              if (date.isValid) {
                onFormChange("moved_to_green_channel_on", date, true);
              }
            } else {
              onFormChange("moved_to_green_channel_on", null);
            }
          }}
        />
      </div>

      {/* Final Review Date */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
            Final Review Date
          </label>
          {formData.final_review && (
            <button
              type="button"
              onClick={() => onFormChange("final_review", null)}
              style={{
                background: "none",
                border: "none",
                color: "#0077b2",
                cursor: "pointer",
                fontSize: "0.813rem",
                padding: "0",
                textDecoration: "underline",
              }}
            >
              Clear
            </button>
          )}
        </div>
        <McInputDate
          hiddenlabel={true}
          required={false}
          placeholder="DD-MM-YYYY"
          format="DD-MM-YYYY"
          value={formData.final_review ? formData.final_review.toFormat('dd-MM-yyyy') : ""}
          name="final_review"
          inputdateselected={(e) => {
            const dateValue = e.detail || e.target?.value;
            if (dateValue) {
              const date = DateTime.fromFormat(dateValue, 'dd-MM-yyyy', { zone: 'utc' });
              if (date.isValid) {
                onFormChange("final_review", date, true);
              }
            }
          }}
        />
      </div>

     

      {/* Deployment Type */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Deployment Type <span style={{ color: "#d32f2f" }}>*</span>
        </label>
        <McSelect
          hiddenlabel={true}
          value={formData.deployment_type}
          optionselected={(e) => onFormChange("deployment_type", e.detail?.value || e.detail)}
          placeholder="Select Deployment Type"
          fit="medium"
        >
          {DEPLOYMENT_OPTIONS.map((option) => (
            <McOption
              key={option.value}
              value={option.value}
              selected={formData.deployment_type === option.value}
            >
              {option.label}
            </McOption>
          ))}
        </McSelect>
      </div>
       {/* Deployment Details */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Deployment Details {formData.deployment_type && formData.deployment_type.toLowerCase() === "automated" && <span style={{ color: "#d32f2f" }}>*</span>}
        </label>
        <McInput
          hiddenlabel={true}
          value={formData.deployment_details}
          input={(e) => onFormChange("deployment_details", e.target.value)}
          placeholder="Enter Deployment Details"
          fit="medium"
        />
      </div>

      {/* Comments - Full Width */}
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <label style={{ fontWeight: "600", fontSize: "0.875rem", color: "#141414" }}>
          Comments
        </label>
        <McTextarea
          hiddenlabel={true}
          value={formData.comments}
          input={(e) => onFormChange("comments", e.target.value)}
          placeholder="Enter Comments"
          rows="4"
          fit="medium"
        />
      </div>
    </div>
  );
}
